import { useEffect, useState } from "react";
import { paper, Point, Size, Rectangle, Path, PointText } from "paper";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import { MULTIPLIER, PADDING, POINTS_OFFSET } from "../utils/constants";
import { toRadians, toDegree } from "../utils/mathUtils";
import { calculateAlpha, calculateQuadrants, calculateDopp, calculateApDir } from "../utils/calculations";
import { applyHorizontalTrace } from "../utils/horizontalTrace";
import DownloadMenu from "../../shared/DownloadMenu";

/**
 * Debug Output Component for Heading View
 * Handles heading projection calculations and visualization
 */
const Debugoutput = (props) => {
  const multiplier = MULTIPLIER;
  const padding = PADDING;
  let points = POINTS_OFFSET;
  const traclength = 100;
  const length = traclength * multiplier;
  const headingrd = props.dimensions.rd * multiplier;
  let scalearr = [];
  let text;
  let i = 0;

  const r = props.dimensions.radiusofcrown * multiplier;
  const height = r * 2;
  const width = r * 2;
  const hw = r * 2;
  const Theta = 0;
  const Arc = (Math.PI * r) / 2;
  const ArcFull = Arc * 2;
  let Ymax = padding + 2 * (Arc + Arc);
  const usrvar = [];

  const tracedimensions = props.dimensions;

  const [, setUserVariables] = useState([]);

  const updateUserVariable = (usrvar) => {
    setUserVariables(usrvar);
  };

  useEffect(() => {
    if (!props.showdebug) {
      paper.setup("paper-canvas");
      creategraph();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dimensions, props.showdebug]);

  const createScale = () => {
    for (i = padding; i <= length + padding; i += multiplier * 10) {
      text = new PointText(new Point(i, Ymax + 15));
      text.justification = "center";
      text.fillColor = "black";
      if (headingrd / multiplier < traclength / 2) {
        text.content = Math.round((i - padding) / multiplier);
      } else {
        text.content = Math.round((i - padding + headingrd) / multiplier - traclength / 2);
      }

      scalearr.push([i, Ymax + 10]);
      scalearr.push([i, Ymax]);
      if (i + multiplier * 10 <= length + padding) {
        for (let j = 1; j <= 9; j++) {
          scalearr.push([i + multiplier * j, Ymax]);
          scalearr.push([i + multiplier * j, Ymax + 5]);
          scalearr.push([i + multiplier * j, Ymax]);
        }
        scalearr.push([i + multiplier * 10, Ymax]);
      }
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });
  };

  const createLog = () => {
    const style = {
      fillColor: "white",
      strokeColor: "black",
      strokeWidth: 1,
    };
    let headinglength;
    if (headingrd / multiplier < traclength / 2) {
      headinglength = headingrd;
    } else {
      headinglength = length / 2;
    }

    const path = new Path.Rectangle(new Rectangle(new Point(padding, padding), new Size(headinglength, 2 * (Arc + Arc))));
    path.style = style;
    const path1 = new Path.Rectangle(new Rectangle(new Point(padding, padding + Arc), new Size(headinglength, 2 * Arc)));
    path1.style = style;

    const dashpath = new Path.Rectangle(
      new Rectangle(new Point(padding + 1 + headinglength, padding), new Size(length - headinglength, 2 * (Arc + Arc)))
    );
    const dashpath1 = new Path.Rectangle(
      new Rectangle(new Point(padding + 1 + headinglength, padding + Arc), new Size(length - headinglength, 2 * Arc))
    );
    dashpath.style = style;
    dashpath.dashArray = [4, 10];
    dashpath1.style = style;
    dashpath1.dashArray = [4, 10];
    const path2 = new Path.Circle(path.bounds.leftCenter.add([-r, 0]), r);
    path2.style = style;

    const path3 = new Path.Line(path2.bounds.leftCenter, dashpath1.bounds.rightCenter);
    path3.strokeColor = "black";
    path3.dashArray = [4, 10];

    // Direction arrows
    const directionarrowline = new Path.Line(path.bounds.topRight.add([-60, -30]), path.bounds.topRight.add([0, -30]));
    directionarrowline.style = style;
    const directionarrowht = new Path.Line(path.bounds.topRight.add([0, -30]), path.bounds.topRight.add([-10, -30]));
    directionarrowht.style = style;
    directionarrowht.rotate(45, path.bounds.topRight.add([0, -30]));
    const directionarrowhb = new Path.Line(path.bounds.topRight.add([0, -30]), path.bounds.topRight.add([10, -30]));
    directionarrowhb.style = style;
    directionarrowhb.rotate(135, path.bounds.topRight.add([0, -30]));
    const directionarrowlineinv = new Path.Line(path.bounds.topLeft.add([0, -30]), path.bounds.topLeft.add([60, -30]));
    directionarrowlineinv.style = style;
    const directionarrowhtinv = new Path.Line(path.bounds.topLeft.add([0, -30]), path.bounds.topLeft.add([-10, -30]));
    directionarrowhtinv.style = style;
    directionarrowhtinv.rotate(135, path.bounds.topLeft.add([0, -30]));
    const directionarrowhbinv = new Path.Line(path.bounds.topLeft.add([0, -30]), path.bounds.topLeft.add([10, -30]));
    directionarrowhbinv.style = style;
    directionarrowhbinv.rotate(45, path.bounds.topLeft.add([0, -30]));

    const path4 = new Path.Arc(path.bounds.topLeft, path.bounds.leftCenter.add([-2 * r, -r / 2]), path.bounds.leftCenter.add([-2 * r, 0]));
    path4.strokeColor = "black";
    path4.dashArray = [14, 5];
    const path5 = new Path.Arc(path.bounds.bottomLeft, path.bounds.leftCenter.add([-2 * r, r / 2]), path.bounds.leftCenter.add([-2 * r, 0]));
    path5.strokeColor = "black";
    path5.dashArray = [14, 5];
    const path6 = new Path.Arc(path1.bounds.topLeft, path.bounds.leftCenter.add([-(r - 3), -r - 3]), path.bounds.leftCenter.add([-r, -r]));
    path6.strokeColor = "black";
    path6.dashArray = [5, 5];
    const path7 = new Path.Arc(path1.bounds.bottomLeft, path.bounds.leftCenter.add([-(r - 3), r + 3]), path.bounds.leftCenter.add([-r, r]));
    path7.strokeColor = "black";
    path7.dashArray = [5, 5];

    // Labels
    text = new PointText(directionarrowline.bounds.topLeft.add([-20, 4]));
    text.justification = "center";
    text.fillColor = "black";
    text.content = "N" + tracedimensions.direction + "\xB0";
    text = new PointText(directionarrowlineinv.bounds.topRight.add([0, 4]));
    text.justification = "left";
    text.fillColor = "black";
    const dopp = parseInt(tracedimensions.direction) > 180 ? parseInt(tracedimensions.direction) - 180 : parseInt(tracedimensions.direction) + 180;
    text.content = "N" + dopp + "\xB0";
    text = new PointText(path.bounds.topLeft.add([15 - padding, 25 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "α = Angle between strike and tunnel direction";
    text = new PointText(path.bounds.topLeft.add([265 - padding, 25 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "β = Apparent dip along tunnel";
    text = new PointText(path.bounds.topLeft.add([15 - padding, 45 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "P1 = Ch. Right Invert";
    text = new PointText(path.bounds.topLeft.add([155 - padding, 45 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "P2 = Ch. Right SPL";
    text = new PointText(path.bounds.topLeft.add([295 - padding, 45 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "P3 = Ch. Left Invert";
    text = new PointText(path.bounds.topLeft.add([435 - padding, 45 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "P4 = Ch. Left SPL";
    text = new PointText(path.bounds.topLeft.add([575 - padding, 45 - padding]));
    text.justification = "left";
    text.fillColor = "black";
    text.content = "P5 = Ch. Centerline";
    text = new PointText(path.bounds.bottomLeft.add([0, 40]));
    text.justification = "left";
    text.fillColor = "black";
    text.content =
      "Tunnel direction: " +
      tracedimensions.direction +
      " Height: " +
      height / multiplier +
      " Width: " +
      width / multiplier +
      " Radius: " +
      (r / multiplier).toFixed(1).replace(/\.0$/, "") +
      " Crown circumference: " +
      (ArcFull / multiplier).toFixed(1).replace(/\.0$/, "") +
      " Wall height: " +
      hw / multiplier;
  };

  const createTrace = () => {
    const style = {
      fillColor: "transparent",
      strokeColor: "black",
      strokeWidth: 1,
    };
    let traceunion = [];
    let amt = props.dimensions.amt;
    let D = parseInt(tracedimensions.direction);
    let Dir = parseInt(props.dimensions.dir);

    // Revise Dir / amt slightly when the trace would plot horizontal, mirroring
    // the plotting step so the heading projection stays consistent.
    const horizontal = applyHorizontalTrace(D, Dir, amt, parseInt(tracedimensions.inclination) || 0);
    D = horizontal.D;
    Dir = horizontal.Dir;
    amt = horizontal.amt;

    // Use utility functions for calculations
    const Alpha = calculateAlpha(D, Dir);
    // Alpha is in degrees, convert to radians for Math.tan
    const TanAlpha = Math.tan(toRadians(Alpha));
    // amt and Alpha are in degrees, convert both to radians
    const TanBeta = Math.tan(toRadians(amt)) * Math.sin(toRadians(Alpha));

    const quadrants = calculateQuadrants(D);
    const ApDir = calculateApDir(D, Dir, quadrants);
    const Dopp = calculateDopp(D);

    const H = [];
    const XP = [];
    const YP = [];
    const XP5 = [];
    const YP5 = [];
    let XPmid;

    let headinglength;
    if (headingrd / multiplier < traclength / 2) {
      headinglength = headingrd;
    } else {
      headinglength = length / 2;
    }
    const XP2 = padding + headinglength;
    const Diff = D - Dir;
    let XP4;

    if (ApDir === D) {
      if ((Diff > -360 && Diff < -270) || (Diff > 0 && Diff < 90)) {
        XP4 = XP2 - (r * 2) / TanAlpha;
        XPmid = XP2 - Math.abs(XP2 - XP4) / 2;
      } else {
        XP4 = XP2 + (r * 2) / TanAlpha;
        XPmid = XP2 + Math.abs(XP2 - XP4) / 2;
      }
    } else {
      // Subroutine 2
      const DiffDopp = Dopp - Dir;
      if ((DiffDopp > -360 && DiffDopp < -270) || (DiffDopp > 0 && DiffDopp < 90)) {
        XP4 = XP2 - (r * 2) / TanAlpha;
        XPmid = XP2 - Math.abs(XP2 - XP4) / 2;
      } else {
        XP4 = XP2 + (r * 2) / TanAlpha;
        XPmid = XP2 + Math.abs(XP2 - XP4) / 2;
      }
    }
    let P5;

    // Right wall
    for (let n = 90; n >= Theta; n--) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc - Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        YP[n] = Ymax - (Arc - Arc * ((n - Theta) / (90 - Theta)));
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP5[n] = YP[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }
    usrvar["P1"] = "(" + ((XP5[90] - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + ",0)";
    const P1 = new Point(XP5[90], Ymax);
    const P2 = new Point(XP2, Ymax - Arc);

    // Right crown
    for (let n = Theta; n <= 90; n++) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc + Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XPmid - XP2) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XPmid - XP2) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc + Arc * ((n - Theta) / (90 - Theta)));
      }
      usrvar["Prc" + n] =
        "XP2 = (" +
        (XP2 - padding) / multiplier +
        ") theta=(" +
        Theta +
        ")" +
        "(" +
        (XP5[n] - padding) / multiplier +
        "," +
        (Arc + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier +
        ")";
      YP5[n] = YP[n];
      scalearr.push([XP5[n], YP5[n]]);
      traceunion.push([XP5[n], YP5[n]]);
    }
    usrvar["P2"] = "(" + ((XP2 - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + (Arc / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    usrvar["P5"] = "(" + ((XP5[90] - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + (2 * Arc / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    P5 = new Point(XP5[90], Ymax - 2 * Arc);
    const rcrown = new Path({
      segments: scalearr,
    });

    // Left crown
    scalearr = [];
    for (let n = 90; n >= Theta; n--) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 - Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 - Arc * ((n - Theta) / (90 - Theta)));
      }

      YP5[n] = YP[n];
      scalearr.push([XP5[n], YP5[n]]);
      traceunion.push([XP5[n], YP5[n]]);
      usrvar["Plc" + n] =
        "XP4 = (" +
        (XP4 - padding) / multiplier +
        ") XPmid=(" +
        (XPmid - padding) / multiplier +
        ")" +
        "(" +
        (XP5[n] - padding) / multiplier +
        "," +
        (Arc + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier +
        ")";
    }
    const lcrown = new Path({
      segments: scalearr,
    });
    usrvar["P4"] = "(" + ((XP4 - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + (3 * Arc / multiplier).toFixed(1).replace(/\.0$/, "") + ")";

    // Left wall
    for (let n = Theta; n <= 90; n++) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (parseInt(ApDir) === parseInt(D)) {
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 + Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XPmid - XP4) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XPmid - XP4) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 + Arc * ((n - Theta) / (90 - Theta)));
      }

      YP5[n] = YP[n];
      traceunion.push([XP5[n], YP5[n]]);
      usrvar["Plw" + n] =
        "XP4 = (" +
        (XP4 - padding) / multiplier +
        ")" +
        "(" +
        (XP5[n] - padding) / multiplier +
        "," +
        (Arc + ArcFull - Arc * ((n - Theta) / (90 - Theta))) / multiplier +
        ")";
    }
    usrvar["P3"] = "(" + ((XP5[90] - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + (4 * Arc / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    const P3 = new Point(XP5[90], Ymax - 4 * Arc);
    const P4 = new Point(XP4, Ymax - 3 * Arc);

    const union = new Path({
      segments: traceunion,
      style: style,
    });
    const tracelength = union["length"] / multiplier;
    usrvar["Tracelength"] = tracelength;
    usrvar["Tracecrown"] = (rcrown["length"] + lcrown["length"]) / multiplier;

    // Creating point
    usrvar["XPmid"] = (XPmid - padding) / multiplier;
    let diff;

    let y;
    const pointheight = parseFloat(tracedimensions.pointheight) * multiplier;

    if (tracedimensions.heightfrom === "rightspl") {
      const distancefromcrown = (pointheight * Arc) / r;
      y = P5.y + distancefromcrown;
    }
    if (tracedimensions.heightfrom === "leftspl") {
      const distancefromcrown = (pointheight * Arc) / r;
      y = P5.y - distancefromcrown;
    }
    const circle = new Path.Circle(new Point(headinglength + padding, y), 2);
    const crossing = new Path.Line(new Point(length + padding, y), new Point(0, y));
    if (union.getIntersections(crossing).length > 0) {
      const startx = union.getIntersections(crossing)[0].point.x;
      diff = startx - (headinglength + padding);
      const pointx = union.position.x - diff;
      const pointy = union.position.y;

      union.position = new Point(pointx, pointy);
    }
    circle.style = style;
    circle.fillColor = "black";

    // Logic for preventing curve going outside plot
    const start = new Path.Line(new Point(padding, 0), new Point(padding, 600));
    let interx = union.getIntersections(start);
    if (interx.length === 1) {
      const firstoffset = union.getOffsetOf(interx[0].point);
      const firstcut = union.splitAt(firstoffset);
      if (firstcut.segments[1].point.x < padding) {
        firstcut.opacity = 0;
      } else {
        union.opacity = 0;
      }
    }
    if (interx.length === 2) {
      const firstoffset = union.getOffsetOf(interx[0].point);
      const firstcut = union.splitAt(firstoffset);
      const secondoffset = firstcut.getOffsetOf(interx[1].point);
      const secondcut = firstcut.splitAt(secondoffset);

      if (firstcut.segments[2].point.x < padding) {
        firstcut.opacity = 0;
      } else {
        secondcut.opacity = 0;
        union.opacity = 0;
      }
    }

    usrvar["P1"] = "(" + ((P1.x - diff - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + ",0)";
    usrvar["P2"] = "(" + ((P2.x - diff - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + (hw / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    usrvar["P3"] = "(" + ((P3.x - diff - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + ((2 * hw + ArcFull) / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    usrvar["P4"] = "(" + ((P4.x - diff - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + ((hw + ArcFull) / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    if (P5) {
      usrvar["P5"] = "(" + ((P5.x - diff - padding) / multiplier).toFixed(1).replace(/\.0$/, "") + "," + ((hw + Arc) / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    }
    usrvar["Pmid"] = "(" + (Math.abs(XP2 - XP4) / 2 / multiplier).toFixed(1).replace(/\.0$/, "") + "," + ((hw + Arc) / multiplier).toFixed(1).replace(/\.0$/, "") + ")";
    text = new PointText(new Point(padding, points + padding + 2 * (hw + Arc)));
    text.justification = "left";
    text.fillColor = "black";
    text.content =
      "N" +
      String(Math.round(Dir)).padStart(3, "0") +
      "/" +
      amt +
      "\xB0" +
      " α =" +
      Alpha +
      "\xB0" +
      " β =" +
      toDegree(Math.atan(TanBeta)).toFixed(1).replace(/\.0$/, "") +
      "\xB0" +
      " Trace Length Total =" +
      usrvar["Tracelength"].toFixed(1).replace(/\.0$/, "") +
      " Trace Length of Crown=" +
      usrvar["Tracecrown"].toFixed(1).replace(/\.0$/, "");
    text = new PointText(new Point(padding, points + 15 + padding + 2 * (hw + Arc)));
    text.justification = "left";
    text.fillColor = "black";
    text.content =
      "P1 =" +
      usrvar["P1"] +
      " P2 =" +
      usrvar["P2"] +
      " P3 =" +
      usrvar["P3"] +
      " P4 =" +
      usrvar["P4"] +
      " P5 =" +
      usrvar["P5"] +
      " Pmid =" +
      usrvar["Pmid"];
    points += 35;
    updateUserVariable(usrvar);
  };

  const createCaution = () => {
    const cautionY = points + padding + 2 * (hw + Arc);
    const cautionLabel = new PointText(new Point(padding, cautionY));
    cautionLabel.justification = "left";
    cautionLabel.fillColor = "black";
    cautionLabel.fontWeight = "bold";
    cautionLabel.content = "Caution:";
    const cautionText = new PointText(new Point(padding + cautionLabel.bounds.width + 4, cautionY));
    cautionText.justification = "left";
    cautionText.fillColor = "black";
    cautionText.content = "Discontinuity trace span may be evaluated with care in consideration of its persistence.";
  };

  const draw = () => {
    createScale();
    createLog();
    createTrace();
    createCaution();
  };

  const creategraph = () => {
    paper.project.clear();
    const width = length + 450;
    const height = 600;
    paper.view.viewSize = new Size(width, height);
    draw();
    paper.view.draw();
  };


  return (
    <div id="page">
      <Header />
      <Footer />
      <div className="page-content header-clear-medium">
        <div className="row">
          <div className="col-md-12">
            <button
              onClick={() => {
                props.showinput();
              }}
              className="btn bg-green-dark mw-2 me-3"
            >
              Edit Input
            </button>
            <DownloadMenu filename="circular-heading" screenLabel="Circular Tunnel — Heading" feedbackInputs={props.dimensions} />
          </div>
          <div className="col-md-6"></div>
          <div className="col-md-12 overflow-auto">
            {!props.showdebug ? <canvas id="paper-canvas" resize="true" /> : ""}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Debugoutput;
